import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';

import { TOrder, TOrderType, useFailOrderMutation } from '@entities/orders';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { FormItemWithError } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';

import { IOrderModalContextProps } from '../model/types';

interface IProps<T> {
  type: TOrderType;
  order: T;
  refetch: () => void;
}

// Backend limit for the fail-manually reason
const REASON_MAX_LENGTH = 245;

export const SUCCESS_ORDER_WARNING =
  'This order is successful. Failing it will remove its amount from the merchant\'s statistics and send the merchant a "failed" webhook.';
export const WITHDRAWAL_WARNING =
  'Failing this payout does not recall the funds. If the money has already been sent, it will not be returned.';

const REASON_REQUIRED_ERROR: FetchBaseQueryError = {
  status: 422,
  data: { errors: { reason: ['Reason is required'] } },
};

// FormItemWithError renders only API-shaped errors ({ message } / { errors }); a network-level
// failure (FETCH_ERROR, PARSING_ERROR, …) would otherwise leave the user without any feedback.
const GENERIC_ERROR: FetchBaseQueryError = {
  status: 'CUSTOM_ERROR',
  error: 'Order rejection failed',
  data: { message: 'Failed to reject the order. Please try again.' },
};

type TApiErrorData = { message?: string; errors?: unknown };

const toDisplayError = (error: FetchBaseQueryError | SerializedError | undefined) => {
  const data = error && 'data' in error ? (error.data as TApiErrorData | undefined) : undefined;

  return data?.message || data?.errors ? error : GENERIC_ERROR;
};

export const FailOrder = <T extends TOrder>({ type, order, refetch }: IProps<T>) => {
  const { id, status } = order;

  const warnings = [
    status === 'success' && SUCCESS_ORDER_WARNING,
    type === 'withdrawal' && WITHDRAWAL_WARNING,
  ].filter(Boolean);

  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<FetchBaseQueryError | null>(null);

  const [failOrder, { isLoading, isError, error }] = useFailOrderMutation();

  // Synchronous guard: the disabled Confirm only takes effect after the next render
  const isSubmitting = useRef(false);
  // The modal can be dismissed while the request is in flight; closeModal() pops the top-most
  // modal, so it must only run while this one is still mounted
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const {
    closeModal,
    additionalContext: { setSelectedIds },
  } = useModal<IOrderModalContextProps>();

  const handleChangeReason = (e: ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
    if (reasonError && e.target.value.trim()) setReasonError(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // The submit button is disabled while the field is empty; this guards whitespace-only input.
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setReasonError(REASON_REQUIRED_ERROR);
      return;
    }

    if (isSubmitting.current) return;
    isSubmitting.current = true;

    const result = await failOrder({ type, id, reason: trimmedReason });
    isSubmitting.current = false;

    if ('data' in result) {
      setSelectedIds([]);
      if (isMounted.current) closeModal();
      refetch();
    }
  };

  return (
    <>
      <h2>Reject Order</h2>

      {warnings.length > 0 && (
        <div className='form-warnings'>
          {warnings.map(warning => (
            <div key={warning} className='form-warning'>
              <ErrorOutlineIcon className='form-warning__icon' />
              <p>{warning}</p>
            </div>
          ))}
        </div>
      )}

      <form className='popup-form' onSubmit={handleSubmit}>
        <FormItemWithError
          name='reason'
          secondName='order'
          label='Reason'
          isError={Boolean(reasonError) || isError}
          error={reasonError || toDisplayError(error)}
        >
          <input
            type='text'
            name='reason'
            id='reason'
            value={reason}
            onChange={handleChangeReason}
            maxLength={REASON_MAX_LENGTH}
            placeholder='Why is this order being rejected?'
          />
        </FormItemWithError>

        <div className='form-submit'>
          <button type='submit' disabled={reason === '' || isLoading}>
            Confirm
          </button>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
};
